/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useId,
  useRef,
} from 'react'
import { cn } from '@/utils/cn'

export interface InputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'placeholder' | 'aria-errormessage'
  > {
  start?: React.ReactNode
  end?: React.ReactNode
  label: string
  error?: string
}

export default forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, start, end, error, id, label, ...props },
  forwardRef,
) {
  const _id = useId()
  if (!id) id = _id

  const inputRef = useRef<HTMLInputElement | null>(null)
  const errorId = useId()

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        className={cn(
          'relative py-[9px] items-center group w-full input-border h-[3.75rem] px-4 rounded-xl bg-static-white flex gap-4',
          error && '!border-negative',
        )}
        onClick={() => inputRef?.current?.focus()}
      >
        {start}

        <InnerWrapper wrap={!!(start || end)}>
          <input
            id={id}
            className="flex-1 outline-none focus:mt-auto [&:not(:placeholder-shown)]:mt-auto disabled:font-semibold disabled:bg-transparent peer"
            ref={(ref) => {
              inputRef.current = ref

              if (typeof forwardRef === 'function') {
                forwardRef(ref)
              } else if (forwardRef) {
                forwardRef.current = ref
              }
            }}
            aria-invalid={!!error}
            aria-errormessage={error && errorId}
            /* Required to use :not(:placeholder-shown) (-> see label) */
            placeholder=" "
            {...props}
          />

          <label
            htmlFor={id}
            className={
              'absolute select-none origin-left transition-transform text-l text-tertiary ' +
              'group-focus-within:-translate-y-3 group-focus-within:scale-[66.666%] ' +
              'peer-[&:not(:placeholder-shown)]:-translate-y-3 peer-[&:not(:placeholder-shown)]:scale-[66.666%]'
            }
          >
            {label}
          </label>
        </InnerWrapper>

        {end}
      </div>

      {error && (
        <div id={errorId} className="ml-2 mt-1 text-negative text-s">
          {error}
        </div>
      )}
    </div>
  )
})

function InnerWrapper(props: { wrap: boolean; children: ReactNode }) {
  if (!props.wrap) {
    return props.children
  }

  return <div className="flex items-center flex-1 h-full">{props.children}</div>
}
